/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import useHandleFilterChange from '../hooks/useHandleFilterChange';
import useGetFiles from '../hooks/useGetFiles';
import useBreadCrumbs from '../hooks/useBreadCrumbs';
import useHandleSelectedRows from '../hooks/useHandleSelectedRows';

import constants, { defaultCaseFilter } from 'constants/index';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { presignedURLGet } from '../services';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useDebounce } from 'hooks/useDebounce';
import { convertCaseToFilterFormat } from 'utils/utils';
import { totalCountEvent } from 'services/generalServices';
import { listGlobalTags } from 'services/Settings';
import useGetTags from '../hooks/useGetTags';
import { courtDateEvent, summarizeDocument } from 'services/fileManager/fileManagerServices';

const FileMangerContext = createContext({});

export const useFileManagerContext = () => {
  const context = useContext(FileMangerContext);

  if (context === undefined) {
    throw new Error('useTestContext must be used within TestProvider');
  }

  return context;
};

export const FileManagerContextProvider = (props) => {
  const {
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isClientFileManager,
    isFirmDocuments,
    caseDetails,
    isClosed,
    onMountUnMount,
    onOperationBreak,
  } = props;

  const userContext = useContext(UserDetailsContext);
  const { readClientNotifications, notificationCounts } = useNavbarContext();
  const location = useLocation();
  const { addToast } = useToast();

  const getDefaultFilter = () => {
    // For case files, client files, and lead files, use old simple filter structure
    if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
      return {
        fileType: '',
        tag: '',
        case: isClientFileManager ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '',
        selectedFolder: location?.state?.breadCrumb ? location?.state?.breadCrumb?.value : '',
        fileStatus: '',
      };
    }

    // For global file manager and firm documents, use new filter structure
    let filter = {
      ...defaultCaseFilter,
      case: convertCaseToFilterFormat(userContext?.clientSelectedCase) || '',
      selectedFolder: location?.state?.breadCrumb ? location?.state?.breadCrumb?.value : '',
    };

    // If this is the Firm Documents page, set default filter to "Client Uploads"
    // if (isFirmDocuments) {
    //   filter.uploadedBy = 'client';
    //   filter.is_client_uploaded = true;
    // }

    let file = location?.state?.file;
    if (location?.state?.isFromClientDashboard && file) {
      const caseVal = { label: file?.case_name, id: file?.case_id };
      filter.case = caseVal;
    }

    // If coming from dashboard signatures, set filter to show signed documents
    if (location?.state?.isFromDashboardSignatures) {
      filter.esignature_status = 'SIGNED';
    }

    return filter;
  };
  // hooks
  const { fileManagerFilters, onFilterChange, onTagChange, setFileManagerFilters } = useHandleFilterChange(getDefaultFilter);
  const { files, getFiles, pageLimit, setPageLimit, paginationDirection, onNextPageClick, onPrevPageClick, nextToken, transitionToken } =
    useGetFiles(isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext);
  const { getFileTags, fullTags, isLoading: isTagsLoading } = useGetTags();

  const { breadCrumbs, setBreadCrumbs, handleBreadCrumbs, filterBreadCrumbs } = useBreadCrumbs();
  const { selectedRows, selectAll, handleSingleCheckboxClick, handleSelectAllCheckboxClick, setSelectedRows } = useHandleSelectedRows();

  //states
  const [currentView, setCurrentView] = useState('list');
  const [selectedItem, setSelectedItem] = useState();
  const [fileName, setFileName] = useState('');
  const [deletePermission, setDeletePermission] = useState(true);
  const [versionFileUpload, setVersionFileUpload] = useState(false);
  const [previewList, setPreviewList] = useState([]);
  const [selectedPreviewItem, setSelectedPreviewItem] = useState();
  const [query, setQuery] = useState('');
  const [valueSelected, setValueSelected] = useState(false);

  // modal states
  const [showEsignStatusModal, setEsignStatusModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [tagModalType, setTagModalType] = useState('');
  const [showShareFileModal, setShowShareFileModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [moveItemModal, setMoveItemModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [isCopy, setIsCopy] = useState(false);

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showUploadedVersionsModal, setShowUploadedVersionsModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showCreateDocumentModal, setShowCreateDocumentModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [globalTags, setGlobalTags] = useState(null);
  const [showSummarizeModal, setShowSummarizeModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [courtDateMsg, setCourtDateMsg] = useState(null);

  const [showSendFaxModal, setShowSendFaxModal] = useState(false);
  const [isFirmDocumentsUpload, setIsFirmDocumentsUpload] = useState(false);

  const filterDebounced = useDebounce(query, 400);

  // Check for dashboard signatures navigation and update filters accordingly
  useEffect(() => {
    if (location?.state?.isFromDashboardSignatures) {
      setFileManagerFilters((prevFilters) => ({
        ...prevFilters,
        esignature_status: 'SIGNED',
      }));
    }
  }, [location?.state?.isFromDashboardSignatures, setFileManagerFilters]);

  //For global search redirection and file preview
  useEffect(() => {
    if (location?.state?.file?.file_path) {
      let file = location?.state?.file;
      // need to make additional changes in report generator page to get file url
      presignedURLGet(file?.file_path, file?.version_id, false)
        .then((res) => {
          file.url = res.data?.file_url;
          setShowPreview(true);
          setSelectedItem(file);
          setSelectedPreviewItem(file);
          setPreviewList([file]);
        })
        .catch((err) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'File Not Found');
        });
    }
    if (location?.state?.breadCrumbs) {
      setBreadCrumbs([{ label: location?.state?.breadCrumb?.label, value: location?.state?.breadCrumb?.value }]);
    }
  }, [location?.state]);

  useEffect(() => {
    if (userContext.permissions) {
      if (userContext.getPermission('cases') !== constants.permissions.WRITE && props.casePage) {
        setDeletePermission(false);
      } else if (userContext.getFirmPermission(constants.permissions.DELETE_ITEMS) === false) {
        setDeletePermission(false);
      } else {
        setDeletePermission(true);
      }
    }
  }, [userContext.permissions]);

  //Client portal notification mark as complete
  useEffect(() => {
    if (userContext?.userDetails?.contact_id && isClientFileManager && notificationCounts) {
      Boolean(notificationCounts?.file_count) && readClientNotifications('file', userContext.userDetails.contact_id);
    }
  }, [userContext?.userDetails, notificationCounts, isClientFileManager]);

  useEffect(() => {
    handleGetFiles(pageLimit);
  }, [fileManagerFilters, isClientFileManager, filterDebounced]);

  useEffect(() => {
    getFileTags(caseDetails?.case_id || '');
  }, [caseDetails?.case_id]);

  const getGlobalTags = async () => {
    totalCountEvent('global_tag').then((response) => {
      listGlobalTags(response.data)
        .then((response) => {
          let tagsArray = [];
          response?.data?.global_tag.forEach((item) => {
            tagsArray.push({ name: item.tag, value: item.tag });
          });
          setGlobalTags(tagsArray);
        })
        .catch((error) => {
          console.log(error);
        });
    });
  };

  const handleGetFiles = async (pageLimit, folder_id) => {
    let case_id;
    let shouldIncludeCaseId = true;

    // Handle case_id logic - check isFirmDocuments first
    if (isFirmDocuments) {
      // Firm Documents page - ALWAYS include case_id as undefined regardless of any other filters
      case_id = undefined;
      shouldIncludeCaseId = true;
    } else if (isGlobalFileManager) {
      if (fileManagerFilters?.uploadedBy === 'all' || !fileManagerFilters?.uploadedBy) {
        // All Files - do NOT include case_id at all
        case_id = undefined;
        shouldIncludeCaseId = false;
      } else if (fileManagerFilters?.is_client_uploaded === true) {
        // Client Uploads - do NOT include case_id
        case_id = undefined;
        shouldIncludeCaseId = false;
      } else if (fileManagerFilters?.isFirmUploads) {
        // Firm Uploads - include case_id as undefined
        case_id = undefined;
        shouldIncludeCaseId = true;
      } else if (fileManagerFilters?.forceCaseUndefined) {
        // Legacy firm uploads logic - do NOT include case_id
        case_id = undefined;
        shouldIncludeCaseId = false;
      } else {
        case_id = fileManagerFilters?.case?.id;
        shouldIncludeCaseId = true;
      }
    } else if (isClientFileManager) {
      if (userContext?.clientCaseList?.length === 1) {
        case_id = userContext?.clientCaseList[0]?.case_id;
      } else {
        // For client file manager, use the case from filters or fallback to clientSelectedCase
        case_id = fileManagerFilters?.case?.id || userContext?.clientSelectedCase?.case_id;
      }
      shouldIncludeCaseId = true;
    } else {
      case_id = caseDetails?.case_id;
      shouldIncludeCaseId = true;
    }

    let obj = {
      selectedFolder: folder_id || fileManagerFilters.selectedFolder,
      selectedType: fileManagerFilters.fileType,
      tag: fileManagerFilters.tag,
      query: query,
      pagination_direction: paginationDirection,
    };

    // Only include case_id if it should be included
    // console.log("shouldIncludeCaseId", shouldIncludeCaseId, case_id);
    if (shouldIncludeCaseId && case_id) {
      obj.case_id = case_id;
    }

    // For case files, client files, and lead files, use old field names
    if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
      obj.is_read = fileManagerFilters?.fileStatus;
    } else {
      // For global file manager and firm documents, use new field names
      obj.is_read = fileManagerFilters?.is_read;
      obj.start_date = fileManagerFilters?.start_date;
      obj.end_date = fileManagerFilters?.end_date;

      // Only send is_client_uploaded if it's actually true (for client uploads)
      if (fileManagerFilters?.is_client_uploaded === true) {
        obj.is_client_uploaded = fileManagerFilters?.is_client_uploaded;
      }

      obj.esignature_status = fileManagerFilters?.esignature_status;
    }
    getFiles(obj, currentView, pageLimit);
  };

  function onResetFilter() {
    if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
      // Case files, client files, and lead files - use old simple filter structure
      setFileManagerFilters((preVal) => ({
        fileType: '',
        tag: '',
        case: isClientFileManager ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '',
        selectedFolder: '',
        fileStatus: '',
      }));
    } else {
      // Global file manager and firm documents - use new filter structure
      setFileManagerFilters((preVal) => ({
        fileType: '',
        tag: '',
        case: isClientFileManager ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '',
        selectedFolder: '',
        is_read: '',
        start_date: '',
        end_date: '',
        uploadedBy: isFirmDocuments ? 'client' : '', // Keep default for firm documents
        is_client_uploaded: isFirmDocuments ? false : '', // Keep default for firm documents
        esignature_status: '',
        forceCaseUndefined: false,
        isFirmUploads: false,
      }));
    }
    setQuery('');
    setBreadCrumbs([]);
  }

  const handleSummarizeFile = (rowData, data) => {
    setSelectedItem(rowData);
    if (Object.keys(data)?.length > 0) {
      summarizeDocument(data)
        .then((response) => {
          const res = response?.data;
          setSummaryData(res);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleShowCreateEvent = (rowData, data) => {
    setShowCreateEventModal(true);
    setSelectedItem(rowData);
    if (Object.keys(data)?.length > 0) {
      courtDateEvent(data)
        .then((response) => {
          const res = response?.data;
          setCourtDateMsg(res);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  function hideCreateEventModal() {
    setShowCreateEventModal(false);
    setCourtDateMsg();
  }

  const modals = {
    showPreview,
    setShowPreview,
    showRenameModal,
    setShowRenameModal,
    moveItemModal,
    setMoveItemModal,
    showDeleteModal,
    showBulkDeleteModal,
    showBulkMoveModal,
    setShowDeleteModal,
    setShowBulkDeleteModal,
    setShowBulkMoveModal,
    showShareFileModal,
    setShowShareFileModal,
    tagModalType,
    setTagModalType,
    showAddTag,
    setShowAddTag,
    showFileUploadModal,
    setShowFileUploadModal,
    showUploadedVersionsModal,
    setShowUploadedVersionsModal,
    showCreateFolderModal,
    setShowCreateFolderModal,
    showCreateDocumentModal,
    setShowCreateDocumentModal,
    showSignatureModal,
    setShowSignatureModal,
    isCopy,
    setIsCopy,
    setEsignStatusModal,
    showEsignStatusModal,
    showSummarizeModal,
    setShowSummarizeModal,
    showDiscrepancyModal,
    setShowDiscrepancyModal,
    showCreateEventModal,
    setShowCreateEventModal,

    showSendFaxModal,
    setShowSendFaxModal,
    isFirmDocumentsUpload,
    setIsFirmDocumentsUpload,
  };

  const contextPayload = useMemo(
    () => ({
      // props
      isGlobalFileManager,
      isCaseFileManager,
      isLeadFileManager,
      isClientFileManager,
      isFirmDocuments,
      // filter
      fileManagerFilters,
      onFilterChange,
      onResetFilter,
      // view
      currentView,
      setCurrentView,
      // files
      files,
      getFiles,
      handleGetFiles,
      pageLimit,
      setPageLimit,
      // selected rows
      selectedRows,
      selectAll,
      handleSingleCheckboxClick,
      handleSelectAllCheckboxClick,
      //breadcrumbs
      breadCrumbs,
      setBreadCrumbs,
      handleBreadCrumbs,
      filterBreadCrumbs,
      // selected item
      selectedItem,
      setSelectedItem,
      // filename
      fileName,
      setFileName,
      // case details
      caseDetails,
      isClosed,
      // delete permissions
      deletePermission,
      modals,
      versionFileUpload,
      setVersionFileUpload,
      previewList,
      setPreviewList,
      selectedPreviewItem,
      setSelectedPreviewItem,
      onNextPageClick,
      onPrevPageClick,
      nextToken,
      transitionToken,
      paginationDirection,
      setSelectedRows,
      onTagChange,
      fullTags,
      query,
      setQuery,
      onMountUnMount,
      onOperationBreak,
      setFileManagerFilters,
      globalTags,
      getGlobalTags,
      getFileTags,
      handleSummarizeFile,
      summaryData,
      setSummaryData,
      handleShowCreateEvent,
      hideCreateEventModal,
      courtDateMsg,
      isTagsLoading,
    }),
    [
      fileManagerFilters,
      setFileManagerFilters,
      files,
      pageLimit,
      currentView,
      selectedRows,
      selectAll,
      breadCrumbs,
      showPreview,
      showShareFileModal,
      selectedItem,
      fileName,
      showAddTag,
      caseDetails,
      tagModalType,
      isClosed,
      deletePermission,
      showRenameModal,
      moveItemModal,
      showDeleteModal,
      showBulkDeleteModal,
      showBulkMoveModal,
      showFileUploadModal,
      showUploadedVersionsModal,
      versionFileUpload,
      previewList,
      selectedPreviewItem,
      showCreateFolderModal,
      showCreateDocumentModal,
      nextToken,
      transitionToken,
      paginationDirection,
      showSignatureModal,
      setSelectedRows,
      fullTags,
      query,
      onMountUnMount,
      onOperationBreak,
      valueSelected,
      setValueSelected,
      globalTags,
      getFileTags,
      showSummarizeModal,
      handleSummarizeFile,
      summaryData,
      setSummaryData,
      showDiscrepancyModal,
      handleShowCreateEvent,
      hideCreateEventModal,
      courtDateMsg,
      isTagsLoading,
      isFirmDocumentsUpload,
    ]
  );

  return <FileMangerContext.Provider value={contextPayload} {...props} />;
};
